import { getAgentCreationAssistantAppId } from '../components/agents/AgentCreationAssistant/agentCreationAssistantConfig';
import AgentCreationStep from '../enums/AgentCreationStep';
import { AgentFormData, AnalyticsCategory } from '../interfaces/Agent';
import { WorkflowType } from '../interfaces/App';
import { isAnalyticsFieldsComplete } from './analyticsFields';
import { isWebscrapConfigValid } from './knowledgeHelper';

const isFilled = (value: unknown): boolean =>
  typeof value === 'string' && value.trim().length > 0;

// A category row is complete when name and description are either both
// filled or both empty — matches the pairing rule previously inline in
// AgentForm's onSubmit (the Unknown catch-all category is prepended
// separately and isn't part of this check).
export const isAnalyticsCategoriesComplete = (
  categories: AnalyticsCategory[] | undefined | null
): boolean => {
  if (!categories) return true;
  return categories.every(
    category => isFilled(category.name) === isFilled(category.description)
  );
};

// The steps shown for each workflow, in order. The assistant only helps
// draft a new agent, and only where its app ID is configured; the phone line
// is only picked once, on create.
export const getAgentStepOrder = (
  workflow: WorkflowType
): AgentCreationStep[] =>
  workflow === WorkflowType.CREATE
    ? [
        ...(getAgentCreationAssistantAppId('call')
          ? [AgentCreationStep.ASSISTANT]
          : []),
        AgentCreationStep.BEHAVIOR,
        AgentCreationStep.KNOWLEDGE,
        AgentCreationStep.CAPABILITIES,
        AgentCreationStep.ANALYTICS,
        AgentCreationStep.LINE,
      ]
    : [
        AgentCreationStep.BEHAVIOR,
        AgentCreationStep.KNOWLEDGE,
        AgentCreationStep.CAPABILITIES,
        AgentCreationStep.ANALYTICS,
      ];

type StepCompletenessPredicate = (values: AgentFormData) => boolean;

export const STEP_COMPLETENESS: Record<
  AgentCreationStep,
  StepCompletenessPredicate
> = {
  // Talking to the assistant is optional: nothing it produces is required.
  [AgentCreationStep.ASSISTANT]: () => true,
  // opening_sentence is intentionally excluded: its field is commented out
  // in AgentBehaviorStep (agent reuses default_sentence instead), so it's
  // never filled and would make this step permanently incomplete.
  [AgentCreationStep.BEHAVIOR]: values =>
    isFilled(values.agent_name) &&
    isFilled(values.role_id) &&
    isFilled(values.voice) &&
    isFilled(values.default_sentence) &&
    isFilled(values.default_language),
  // A knowledge base is optional: opting out completes the step outright.
  [AgentCreationStep.KNOWLEDGE]: values =>
    values.knowledge_mode === 'none' ||
    isFilled(values.existing_knowledge_id) ||
    (isFilled(values.knowledge_name) &&
      isFilled(values.knowledge_language) &&
      isWebscrapConfigValid(values)),
  // No required fields today — see docs/specs/agent-form-click-to-jump-navigation.md
  [AgentCreationStep.CAPABILITIES]: () => true,
  [AgentCreationStep.ANALYTICS]: values =>
    isFilled(values.analytics_language) &&
    isAnalyticsCategoriesComplete(values.call_categories) &&
    isAnalyticsFieldsComplete(values.analytics_fields),
  // The step offers a mutually-exclusive choice (attach an existing number
  // vs. provision a new one); the choice itself is local UI state in
  // AgentLineStep, not a form field, so it's inferred here from which of
  // the two backing fields is filled.
  [AgentCreationStep.LINE]: values =>
    isFilled(values.custom_phone_number)
      ? true
      : isFilled(values.system_phone_number_country_code),
};

export type StepCompletenessOptions = {
  /** MixVoIP iframe: SIP/extension is assigned in the cockpit, not here. */
  skipPhoneLineRequirement?: boolean;
};

export const isStepComplete = (
  step: AgentCreationStep,
  values: AgentFormData,
  options?: StepCompletenessOptions
): boolean => {
  if (step === AgentCreationStep.LINE && options?.skipPhoneLineRequirement) {
    return true;
  }
  return STEP_COMPLETENESS[step](values);
};

export const firstIncompleteStep = (
  values: AgentFormData,
  stepOrder: AgentCreationStep[],
  options?: StepCompletenessOptions
): AgentCreationStep | undefined =>
  stepOrder.find(step => !isStepComplete(step, values, options));
